import { useState, useEffect } from "react";
import "@/App.css";
import { useQuery } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { getDashboard, getNews } from "@/lib/api";

import IslandPulse from "@/components/IslandPulse";
import RoutesTable from "@/components/RoutesTable";
import FlightsMini from "@/components/FlightsMini";
import SchoolMobilityPanel from "@/components/SchoolMobilityPanel";
import WeatherPanel from "@/components/WeatherPanel";
import HazardPanel from "@/components/HazardPanel";
import NewsFeed from "@/components/NewsFeed";
import RadioPlayer from "@/components/RadioPlayer";
import ShareCard from "@/components/ShareCard";
import RouteModal from "@/components/RouteModal";

import OperationsMap from "@/pages/OperationsMap";
import SenderPage from "@/pages/SenderPage";
import PublicStatusPage from "@/pages/PublicStatusPage";
import PublicApp from "@/pages/PublicApp";
import FieldApp from "@/pages/FieldApp";

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const time = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "America/Barbados" });
  return <div className="hdr-clock" data-testid="header-clock"><span className="live-dot" /> {time} AST</div>;
}

const dirIcon = (d) => (d === "positive" || d === "up" ? "fa-arrow-trend-up" : d === "negative" || d === "down" ? "fa-arrow-trend-down" : "fa-arrow-right-long");

function PredictChip({ icon, text, dir = "neutral", testid }) {
  if (!text) return null;
  return (
    <div className={`predict-chip pc-${dir}`} data-testid={testid}>
      <span className="pc-ic"><i className={`fa-solid ${icon}`} /></span>
      <span className="pc-fore"><i className={`fa-solid ${dirIcon(dir)}`} /> forecast</span>
      <span className="pc-text">{text}</span>
    </div>
  );
}

function Dashboard() {
  const [selectedRoute, setSelectedRoute] = useState(null);
  const { data: dashboard, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: getDashboard, refetchInterval: 60000 });
  const { data: news } = useQuery({ queryKey: ["news"], queryFn: getNews, refetchInterval: 120000 });

  if (isLoading || !dashboard) {
    return (
      <div className="App">
        <div className="loading-screen"><div className="loader"><div className="spin" /><p>Reading the island…</p></div></div>
      </div>
    );
  }

  const pred = dashboard.predictions || {};

  return (
    <div className="App pulse-app">
      <div className="control-room pulse-room">
        <header className="pulse-header" data-testid="pulse-header">
          <div className="brand">
            <div className="brand-mark"><i className="fa-solid fa-sun" /></div>
            <div className="brand-text">
              <h1>Barbados Live</h1>
              <span>Island Pulse · what&apos;s happening right now</span>
            </div>
          </div>
          <div className="hdr-right">
            <ShareCard mood={dashboard.mood} weather={dashboard.weather} story={(news?.items || [])[0]} />
            <Clock />
          </div>
        </header>

        <section className="cr-hero" data-testid="hero-pulse">
          <IslandPulse mood={dashboard.mood} prediction={pred} />
        </section>

        <div className="pulse-grid" data-testid="pulse-grid">
          <div className="pulse-col" data-testid="col-traffic">
            <div className="col-label"><i className="fa-solid fa-car-side" /> Traffic &amp; Flights</div>
            <PredictChip icon="fa-car-side" text={pred.traffic?.text} dir={pred.traffic?.direction} testid="predict-traffic" />
            <div className="col-scroll">
              <RoutesTable routes={dashboard.routes} onSelect={setSelectedRoute} />
              <FlightsMini />
              <SchoolMobilityPanel weather={dashboard.weather} />
            </div>
          </div>

          <div className="pulse-col" data-testid="col-weather">
            <div className="col-label"><i className="fa-solid fa-cloud-sun-rain" /> Weather</div>
            <PredictChip icon="fa-umbrella" text={pred.weather?.text} dir={pred.weather?.direction} testid="predict-weather" />
            <div className="col-scroll">
              <WeatherPanel weather={dashboard.weather} />
              <HazardPanel hazards={dashboard.hazards} />
            </div>
          </div>

          <div className="pulse-col" data-testid="col-news">
            <div className="col-label"><i className="fa-solid fa-satellite-dish" /> News &amp; Radio</div>
            <PredictChip icon="fa-calendar-star" text={pred.news?.text} dir="neutral" testid="predict-news" />
            <div className="col-scroll">
              <NewsFeed news={news?.items || []} />
              <RadioPlayer />
            </div>
          </div>
        </div>
      </div>

      {selectedRoute && <RouteModal routeId={selectedRoute} onClose={() => setSelectedRoute(null)} />}
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/ops" element={<OperationsMap />} />
        <Route path="/sender" element={<SenderPage />} />
        <Route path="/status" element={<PublicStatusPage />} />
        <Route path="/go" element={<PublicApp />} />
        <Route path="/field" element={<FieldApp />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
